import { Injectable } from '@angular/core';
import { AppConfigService } from '@app/services/config/app-config.service';
import { TokensService } from '@app/rest-client/faxe/services/tokens.service';
import { BehaviorSubject, combineLatestWith, Observable, take } from 'rxjs';
import { SessionService } from '@app/services/session/session.service';
import { SessionKeys } from '@app/constants/session-keys';
import { defined, isNotEmpty } from '@app/fremework/functions';
import { UserToken, Authorization } from '@app/services/password/model/user-token';
import { filter, map } from 'rxjs/operators';
import { SelectedRaceService } from '@app/services/selected-race.service';

@Injectable({
  providedIn: 'root'
})
export class PasswordService {
  private tokenSubject = new BehaviorSubject<UserToken[]>(null);
  public tokens$: Observable<UserToken[]> = this.tokenSubject.asObservable();
  private tokenRefreshTimer: any;
  constructor(private readonly tokensService: TokensService,
              private sessionService: SessionService,
              private selectedRaceService: SelectedRaceService) {
  }


  public startTokenRefreshTimer(raceGuid: string, expiresInMs: number): void {
    if (this.tokenRefreshTimer) {
      clearInterval(this.tokenRefreshTimer);
    }

    // Förnya 5 minuter innan expiration
    const refreshTimeMs = expiresInMs - (5 * 60 * 1000);
    const delayUntilRefresh = refreshTimeMs - Date.now();

    console.log('delayUntilRefresh', delayUntilRefresh, new Date(delayUntilRefresh).toISOString(), 'expiresInMs', expiresInMs, new Date(expiresInMs).toISOString());

    if (delayUntilRefresh > 0) {
      console.log('Starting token refresh timer. RaceGuid', raceGuid);
      this.tokenRefreshTimer = setTimeout(() => {
        console.log('Token expiring soon, refreshing. Delay until refreshing...', delayUntilRefresh);
        this.refreshToken(raceGuid);
      }, delayUntilRefresh);
    }
  }

  private refreshToken(raceGuid: string): void {
    const userToken = this.getUserToken(raceGuid);
    if (userToken) {
      this.requestToken(userToken.user, '', userToken.signature, raceGuid)
        .pipe(take(1))
        .subscribe(newToken => {
            console.log('Token refreshed successfully');
            this.setToken(newToken);
          },
          (error) => {
            console.error('Failed to refresh token:', error);
            this.getToken(raceGuid);
          }
        );
    } else {
      console.log('No user token found to refresh for raceGuid:',this.user, raceGuid);
    }
  }

  raceUserToken(): Observable<UserToken> {
    return this
      .tokens$
      .pipe(
        filter((userTokens: UserToken[]) => isNotEmpty(userTokens)),
        combineLatestWith(this.selectedRaceService.selectedRace$
          .pipe(
            filter(race => defined(race)))
        ),
        map(([userTokens, selectedRace]) => {
            const userToken = PasswordService.findUserToken(userTokens, selectedRace.raceGuid, this.user);
            userToken.selectedRaceGuid = selectedRace.raceGuid;
            return userToken;
          }
        )
      );
  }

  public static findUserToken(usertTokens: UserToken[], raceGuid: string, user: String): UserToken {
    return usertTokens?.filter(userToken =>
      userToken.user === user)
      .find(usertToken => defined(usertToken.authorization
        .find(aut => aut.raceGuid === raceGuid)));
  }

  get user(): string {
    return this.sessionService.get(SessionKeys.user);
  }

  getToken(raceGuid: string): string {
    if (!defined(raceGuid)) {
      return undefined;
    }
    const userToken = this.getUserToken(raceGuid);
    if (this.isExpired(userToken)) {
      console.log('Token is expired');
      // ta bort endast denna token, behåll övriga
      const allTokens = this.fromJson(this.sessionService.get(SessionKeys.tokens)) || [];
      const filtered = allTokens.filter(t =>
        !(t.user === userToken.user && t.selectedRaceGuid === userToken.selectedRaceGuid)
      );
      this.sessionService.set(SessionKeys.tokens, this.asJson(filtered));
      this.tokenSubject.next(filtered);
      this.refreshToken(raceGuid);
      return undefined;
    }
    return defined(userToken) ? userToken.token : undefined;
  }

  getUserToken(raceGuid: string): UserToken {
    const user = this.user;

    if (!defined(user)) {
      return undefined;
    }
    return this.findUserToken(user, raceGuid);
  }

  setToken(userToken: UserToken): void {

    console.log('settoken', userToken);
    if (!this.sessionService.hasKey(SessionKeys.tokens)) {
      this.sessionService.set(SessionKeys.tokens, this.asJson([]));
    }

    const userTokens: UserToken[] = this.fromJson(this.sessionService.get(SessionKeys.tokens))
      .filter(t => t.user !== userToken.user);

    userTokens.push(userToken);
    this.sessionService.set(SessionKeys.tokens, this.asJson(userTokens));

    this.tokenSubject.next(userTokens);

    // Starta auto-refresh om token har expiration
    if (userToken.expiresIn) {
      this.startTokenRefreshTimer(userToken.selectedRaceGuid, userToken.expiresIn);
    }
  }

  init(): void {
    const tokens = this.fromJson(this.sessionService.get(SessionKeys.tokens));

    if (defined(tokens && isNotEmpty(tokens))) {
      this.tokenSubject.next(tokens);
    }
  }

  asJson(userTokens: UserToken[]): string {
    return JSON.stringify(userTokens);
  }

  findUserToken(user: string, raceGuid: string): UserToken {
    return this.fromJson(this.sessionService.get(SessionKeys.tokens))
      ?.find(userToken => userToken.user === user &&
        userToken.authorization.find(a => a.raceGuid === raceGuid));
  }

  fromJson(json: string): UserToken[] {
    return JSON.parse(json);
  }


  isViewer(): boolean {
    return !!AppConfigService.environmentConfigData?.viewers.find(v => v.url === window.location.hostname);
  }

  requestToken(
    user: string,
    password: string,
    signature: string,
    raceGuid: string): Observable<UserToken> {

    this.sessionService.set(SessionKeys.user, user);

    return this.tokensService.postRequestToken({
      userName: user,
      password: password,
      signature: signature
    })
      .pipe(map(token => {
        const jwt = token?.access;
        const authorization = PasswordService.parseAuthorizationFromJwt(jwt);

        return {
          user,
          token: jwt,
          signature,
          selectedRaceGuid: raceGuid,
          authorization,
          expiresIn: token?.atExp ? new Date(token.atExp).getTime() : undefined
        }
      }));
  }

  private isExpired(t: UserToken): boolean {
    if (!t?.expiresIn) return false;
    const nowMs = Date.now();
    return t.expiresIn <= nowMs;
  }

  private static parseAuthorizationFromJwt(jwt: string): Authorization[] {
    if (!jwt) {
      return [];
    }

    try {
      const parts = jwt.split('.');
      if (parts.length < 2) {
        return [];
      }
      const payload = parts[1];

      // base64url -> base64
      const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
      // Add padding if necessary
      const pad = base64.length % 4;
      const padded = base64 + (pad ? '='.repeat(4 - pad) : '');

      const json = decodeURIComponent(Array.prototype.map.call(atob(padded), function(c: any) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      }).join(''));

      const obj = JSON.parse(json);

      // The token payload contains a field "raceauthjson" which itself is a JSON string
      // representing an array of { Key: string, Value: string[] } objects.
      const raw = obj?.raceauthjson;
      if (!raw) {
        return [];
      }

      const pairs = JSON.parse(raw);
      if (!Array.isArray(pairs)) {
        return [];
      }

      return pairs.map((p: any) => ({
        raceGuid: p.Key,
        roles: p.Value || []
      } as Authorization));
    } catch (e) {
      console.error('Failed to parse JWT authorization payload', e);
      return [];
    }
  }

}
